<template>
  <div class="space-y-11">
    <div class="flex flex-row items-center justify-between">
      <span class="text-2xl md:text-3xl font-medium sm:text-5xl">{{ __('Challenges')}}</span>
      <Button
          class="hover-lift flex items-center gap-1.5"
          type="submit"
          transition="transition-all"
          @click="redirectToPage('redesign.company.challenges.create')"
      >
        <span>
          {{ __('New Challenge') }}
        </span>
      </Button>
    </div>
    <TabGroup
        as="div"
        :selectedIndex="selectedIndex"
        @change="handleTabChange">
      <TabList class="hidden sm:flex gap-2 overflow-auto rounded-lg bg-white p-3">
        <Tab v-slot="{ selected }" as="template">
          <button
              :class="selected ? 'bg-secondary/5':'bg-white'"
              class="rounded-md px-2 py-1 md:px-8 md:py-3 font-medium leading-normal text-zinc-800 outline-none transition-colors hover:bg-secondary/5"
          >
            {{ __('My Active Challenges') }}
          </button>
        </Tab>
        <Tab v-slot="{ selected }" as="template">
          <button
              :class="selected? 'bg-secondary/5':'bg-white'"
              class="rounded-md px-8 py-3 font-medium leading-normal text-zinc-800 outline-none transition-colors hover:bg-secondary/5"
          >
            {{ __('My Inactive Challenges') }}
          </button>
        </Tab>
        <Tab v-slot="{ selected }" as="template">
          <button
              :class="selected? 'bg-secondary/5':'bg-white'"
              class="rounded-md px-8 py-3 font-medium leading-normal text-zinc-800 outline-none transition-colors hover:bg-secondary/5"
          >
            {{ __('All Company Challenges') }}
          </button>
        </Tab>
      </TabList>

      <div class="flex sm:hidden items-center justify-between bg-white w-full px-2 py-3.5 rounded-lg">
        <button
            v-if="mobileTabIndex > 0"
            @click="previousMobileTab"
            class="flex-shrink-0 mr-2"
        >
          <Icon name="arrow_back_ios" class="w-4 h-5 text-zinc-800" is-filled />
        </button>

        <span class="text-center flex-1 rounded-md font-medium leading-normal text-zinc-800 bg-secondary/5 py-3">
          <span
              :class="[
                selectedIndex === 0 ? 'pl-3' : selectedIndex === 1 ? '' : 'pr-3'
            ]"
          >
            {{ selectedIndex === 0 ? __('My Active Challenges') : selectedIndex === 1 ? __('My Inactive Challenges') : __('All Company Challenges') }}
          </span>
        </span>

        <button
            v-if="mobileTabIndex < availableTabs.length - 1"
            @click="nextMobileTab"
            class="flex-shrink-0 ml-2"
        >
          <Icon name="arrow_forward_ios" class="w-4 h-5 text-zinc-800" is-filled />
        </button>
      </div>
      <TabPanels class="bg-white mt-4 md:mt-12 p-6 rounded-lg">
        <TabPanel v-for="tab in availableTabs" :key="tab">
          <div v-if="loading" class="flex justify-center items-center py-12">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500"></div>
          </div>
          <EmptyState
              v-else-if="!challenges.length"
              :title="__('No challenges found')"
              :subtitle="searchQuery ? __('Try adjusting your search terms') : __('There are no challenges running currently, but you can still search for suitable job openings.')"
              :button-text="__('Go to Jobs')"
              button-route="redesign.jobs.index"
          />
          <div v-else>
            <div class="relative mb-6 w-full">
              <input
                  id="submission-search"
                  ref="searchInput"
                  v-model="searchQuery"
                  type="text"
                  :placeholder="__('Search')"
                  class="w-full bg-white rounded-lg border border-gray-300 px-4 py-2.5 pl-12 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-base"
                  @input="handleSearch"
                  autocomplete="off"
              />

              <span class="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-zinc-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <circle cx="11" cy="11" r="6"></circle>
                  <path d="M21 21l-4.35-4.35"></path>
                </svg>
              </span>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
              <DeleteModal
                  :open="showDeleteModal"
                  :title="__('Delete this challenge?')"
                  :message="__('This action will permanently delete the challenge. Are you sure you want to proceed?')"
                  :confirmText="__('Yes, Delete')"
                  :cancelText="__('Cancel')"
                  @confirm="handleDeleteConfirmed"
                  @cancel="showDeleteModal = false"
              />
              <ChallengeCard
                  v-for="ch in challenges"
                  :key="ch.id"
                  :challenge="ch"
                  @delete="deleteChallenge(ch.id)"
                  @toggle-status="toggleStatus(ch.id)"
              />
            </div>
            <div v-if="lastPage > 1" class="mt-8">
              <Pagination
                  :current-page="currentPage"
                  :last-page="lastPage"
                  @update:currentPage="handlePageChange"
              />
            </div>
          </div>
        </TabPanel>
      </TabPanels>
    </TabGroup>
  </div>
</template>

<script setup>
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from '@headlessui/vue';
import {__} from "@/helpers";
import {Button} from "@/components/block/button";
import ChallengeCard from "@/pages/company/challenges/components/ChallengeCard.vue";
import {computed, onMounted, ref} from "vue";
import {DeleteModal} from "@/components/other/delete-modal";
import axios from "axios";
import { useToast } from 'vue-toastification';
import EmptyState from "@/pages/candidate/challenges/components/EmptyState.vue";
import {Pagination} from "@/components/global";
import {Icon} from "@/components/block/icon";

const props = defineProps({
  data: {
    type: Object,
    required: true
  }
});

const availableTabs = ['active','inactive','all'];

const getInitialTabIndex = ()=> {
  const filter = props.data.filter || 'active';
  return availableTabs.indexOf(filter) >= 0 ? availableTabs.indexOf(filter) : 0;
}
const selectedIndex = ref(getInitialTabIndex());
const toast = useToast();

const searchQuery = ref(props.data.search || '');
const currentPage = ref(props.data.challenges?.meta?.current_page || 1);
const loading = ref(false);
const challengesData = ref(props.data.challenges);

const challenges = computed(() => challengesData.value?.data || []);
const lastPage = computed(() => challengesData.value?.meta?.last_page || 1);

const searchTimeout = ref(null);

const mobileTabIndex = ref(selectedIndex.value);

const nextMobileTab = () => {
  if (mobileTabIndex.value < availableTabs.length - 1) {
    mobileTabIndex.value++;
    selectedIndex.value = mobileTabIndex.value;
    handleTabChange(mobileTabIndex.value);
  }
}

const previousMobileTab = () => {
  if (mobileTabIndex.value > 0){
    mobileTabIndex.value--;
    selectedIndex.value = mobileTabIndex.value;
    handleTabChange(mobileTabIndex.value);
  }
}
const handleSearch = () => {
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value);
  }

  searchTimeout.value = setTimeout(() => {
    currentPage.value = 1;
    fetchChallenges();
  }, 300);
}

const handlePageChange = (page) => {
  currentPage.value = page;
  fetchChallenges();
}

function handleTabChange(idx) {
  selectedIndex.value = idx;
  currentPage.value = 1;
  searchQuery.value = '';
  fetchChallenges();
}

const redirectToPage = (name, params) => {
  window.location.href = route(name, params);
};

const showDeleteModal = ref(false);
let challengeId = null;

function deleteChallenge(id) {
  challengeId = id;
  showDeleteModal.value = true;
}

const handleDeleteConfirmed = async () => {
  showDeleteModal.value = false;
  try {
    const response = await axios.delete(
        route('redesign.company.challenges.destroy', { challenge: challengeId })
    );

    await fetchChallenges();

    toast.success(response.data.message);
  } catch (error) {
    toast.error(
        error.response?.data?.message || __('Error deleting challenge')
    );
  }
};

const toggleStatus = async (id) => {
  try {
    const response = await axios.patch(
        route('redesign.company.challenges.toggle-status', { challenge: id })
    );

    await fetchChallenges();

    toast.success(response.data.message);
  } catch(error) {
    toast.error(
        error.response?.data?.message || __('Error changing the status of the challenge')
    );
  }
}

const fetchChallenges = async () => {
  loading.value = true;
  try {
    const params = {
      page: currentPage.value,
      search: searchQuery.value,
      filter: availableTabs[selectedIndex.value],
    };

    const response = await axios.get(route('company.challenges'), { params });

    challengesData.value = response.data.data.challenges;
  } catch (error) {
    if (error.response?.status === 403) {
      toast.error(error.response?.data?.message || __('You do not have permission to view all company challenges.'));
    } else {
      toast.error(__('Error fetching challenges.'));
    }
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  if (!props.data.challenges) {
    fetchChallenges();
  }
});
</script>
